import { useEffect, useMemo, useState } from "react";

import { SITES, type Site } from "@hana/db";

import { CRAWLER_IMPLEMENTED_SITES, CRAWLER_PENDING_SITES, DEFAULT_CRAWL_SITE } from "./sites.js";

type Job = {
  id: number;
  site: Site;
  status: "pending" | "running" | "done" | "failed";
  requestedBy: string;
  attempts: number;
  errorMessage: string | null;
  createdAt: string;
  finishedAt: string | null;
};

type Offer = {
  id: number;
  site: Site;
  title: string;
  pointText: string;
  url: string;
  crawledAt: string;
};

type AffiliateTag = {
  id: number;
  site: Site;
  tag: string;
  updatedAt: string;
};

// /api/agent が返す案件の形。site は 51 brand まで拡張済みなので string で受ける。
type AgentItem = {
  id: number;
  site: string;
  title: string;
  description?: string;
  pointText: string;
  url: string;
  rank?: number;
  similarity?: number;
};

type AgentTraceEvent =
  | { type: "thought"; text: string }
  | {
      type: "tool_call";
      tool: "keyword_search" | "vector_search";
      query: string;
      hits: number;
    };

const DEFAULT_AGENT_QUERY = "楽天カードと年会費無料のクレカ、どっちがお得?";

const fmt = (value: string | null) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

function TraceTimeline({ trace }: { trace: AgentTraceEvent[] }) {
  return (
    <ol className="agentTrace">
      {trace.map((event, i) => (
        <li key={i} className={event.type}>
          {event.type === "thought" ? (
            <>
              <span className="traceLabel">思考</span>
              <span className="traceText">{event.text}</span>
            </>
          ) : (
            <>
              <span className="traceLabel">
                {event.tool === "vector_search" ? "ベクトル検索" : "キーワード検索"}
              </span>
              <span className="traceText">
                「{event.query}」 → {event.hits}件
              </span>
            </>
          )}
        </li>
      ))}
    </ol>
  );
}

export function App() {
  const [site, setSite] = useState<Site>(DEFAULT_CRAWL_SITE);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [affiliateTags, setAffiliateTags] = useState<Record<Site, string>>(
    () => Object.fromEntries(SITES.map((s) => [s, ""])) as Record<Site, string>,
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  // 51 サイト分の行をそのまま並べると探せないので、サイト名の部分一致で絞り込む
  const [tagFilter, setTagFilter] = useState("");

  // エージェント検索用の state は既存の busy / message とは別に持ち、
  // 10 秒ポーリングに巻き込まれないようにする。
  const [agentQuery, setAgentQuery] = useState(DEFAULT_AGENT_QUERY);
  const [agentBusy, setAgentBusy] = useState(false);
  const [agentAnswer, setAgentAnswer] = useState("");
  const [agentItems, setAgentItems] = useState<AgentItem[]>([]);
  const [agentTrace, setAgentTrace] = useState<AgentTraceEvent[]>([]);
  const [agentError, setAgentError] = useState("");

  const failedJobs = useMemo(() => jobs.filter((x) => x.status === "failed").length, [jobs]);

  const crawlerReady = useMemo(() => CRAWLER_IMPLEMENTED_SITES.includes(site), [site]);

  const filteredTagSites = useMemo<readonly Site[]>(() => {
    const q = tagFilter.trim().toLowerCase();
    return q ? SITES.filter((s) => s.includes(q)) : SITES;
  }, [tagFilter]);

  const loadAll = async () => {
    const [jobsRes, offersRes] = await Promise.all([fetch("/api/jobs"), fetch("/api/offers")]);
    if (!jobsRes.ok || !offersRes.ok) {
      throw new Error("failed to fetch dashboard data");
    }

    const jobsJson = (await jobsRes.json()) as { items: Job[] };
    const offersJson = (await offersRes.json()) as { items: Offer[] };

    setJobs(jobsJson.items ?? []);
    setOffers(offersJson.items ?? []);
  };

  // 編集中の入力をポーリングで上書きしないよう、タグは初回のみ取得する
  const loadAffiliateTags = async () => {
    const res = await fetch("/api/affiliate-tags");
    if (!res.ok) {
      throw new Error("failed to fetch affiliate tags");
    }
    const json = (await res.json()) as { items: AffiliateTag[] };
    setAffiliateTags((prev) => {
      const next = { ...prev };
      for (const item of json.items ?? []) next[item.site] = item.tag;
      return next;
    });
  };

  const saveAffiliateTag = async (targetSite: Site) => {
    setBusy(true);
    setMessage("");
    try {
      const res = await fetch("/api/affiliate-tags", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ site: targetSite, tag: affiliateTags[targetSite] }),
      });
      if (!res.ok) {
        throw new Error(`save failed: ${res.status}`);
      }
      setMessage(
        affiliateTags[targetSite].trim()
          ? `${targetSite} のアフィリエイトタグを保存しました。`
          : `${targetSite} のアフィリエイトタグを削除しました。`,
      );
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "unknown error");
    } finally {
      setBusy(false);
    }
  };

  const enqueue = async () => {
    setBusy(true);
    setMessage("");
    try {
      const res = await fetch("/api/jobs", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ site }),
      });
      if (!res.ok) {
        throw new Error(`enqueue failed: ${res.status}`);
      }
      await loadAll();
      setMessage(`${site} のクロールジョブを追加しました。`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "unknown error");
    } finally {
      setBusy(false);
    }
  };

  const retry = async (jobId: number) => {
    setBusy(true);
    setMessage("");
    try {
      const res = await fetch(`/api/jobs/${jobId}/retry`, { method: "POST" });
      if (!res.ok) {
        throw new Error(`retry failed: ${res.status}`);
      }
      await loadAll();
      setMessage(`job #${jobId} を再実行キューに戻しました。`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "unknown error");
    } finally {
      setBusy(false);
    }
  };

  // SSE で trace をリアルタイム受信し、最後に answer + items を確定させる。
  const runAgent = () => {
    const q = agentQuery.trim();
    if (!q) {
      setAgentAnswer("");
      setAgentItems([]);
      setAgentTrace([]);
      setAgentError("");
      return;
    }

    setAgentBusy(true);
    setAgentError("");
    setAgentAnswer("");
    setAgentItems([]);
    setAgentTrace([]);

    const es = new EventSource(`/api/agent?q=${encodeURIComponent(q)}&stream=1`);
    const finish = () => {
      es.close();
      setAgentBusy(false);
    };

    es.addEventListener("trace", (e) => {
      try {
        const event = JSON.parse((e as MessageEvent).data) as AgentTraceEvent;
        setAgentTrace((prev) => [...prev, event]);
      } catch {
        // 壊れたイベントは黙って捨てる
      }
    });
    es.addEventListener("result", (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data) as {
          answer: string;
          items: AgentItem[];
        };
        setAgentAnswer(data.answer ?? "");
        setAgentItems(data.items ?? []);
      } catch {
        setAgentError("結果の解析に失敗しました。");
      } finally {
        finish();
      }
    });
    es.addEventListener("failure", (e) => {
      let msg = "agent search failed";
      try {
        const data = JSON.parse((e as MessageEvent).data) as { message?: string };
        if (data.message) msg = data.message;
      } catch {
        // fallthrough
      }
      setAgentError(msg);
      finish();
    });
    es.onerror = () => {
      setAgentError((prev) => prev || "接続が中断されました。");
      finish();
    };
  };

  useEffect(() => {
    void loadAll().catch((e) => setMessage(e instanceof Error ? e.message : "failed to load"));
    void loadAffiliateTags().catch((e) =>
      setMessage(e instanceof Error ? e.message : "failed to load affiliate tags"),
    );
    const timer = setInterval(() => {
      void loadAll().catch(() => undefined);
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  return (
    <main>
      <section className="header">
        <h1>管理ダッシュボード</h1>
        <p>ジョブ投入、失敗ジョブの再実行、案件取り込み結果を監視します。</p>
      </section>

      <section className="control">
        <div className="row">
          {/* 51 サイトのうちクローラ実装済みは一部なので optgroup で分ける */}
          <select
            className="select siteSelect"
            value={site}
            onChange={(e) => setSite(e.currentTarget.value as Site)}
          >
            <optgroup label={`クローラ実装済み (${CRAWLER_IMPLEMENTED_SITES.length})`}>
              {CRAWLER_IMPLEMENTED_SITES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </optgroup>
            <optgroup label={`クローラ未実装 (${CRAWLER_PENDING_SITES.length})`}>
              {CRAWLER_PENDING_SITES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </optgroup>
          </select>
          <button
            type="button"
            className="btn primary"
            onClick={() => void enqueue()}
            disabled={busy}
          >
            ジョブ追加
          </button>
          <button type="button" className="btn" onClick={() => void loadAll()} disabled={busy}>
            再読込
          </button>
        </div>
        {crawlerReady ? null : (
          <div className="notice">
            {site} はまだクローラ未実装です。ジョブは登録できますが、取得 0 件で完了します。manifest
            にクロール先 URL が未登録の site の場合はジョブ自体が失敗します。
          </div>
        )}
        <div className="stats">
          <div className={`stat${failedJobs > 0 ? " alert" : ""}`}>
            <span className="label">失敗ジョブ</span>
            <span className="value">{failedJobs}</span>
          </div>
          <div className="stat">
            <span className="label">総ジョブ</span>
            <span className="value">{jobs.length}</span>
          </div>
          <div className="stat">
            <span className="label">取得案件</span>
            <span className="value">{offers.length}</span>
          </div>
        </div>
        {message ? <div className="message">{message}</div> : null}
      </section>

      <section className="panel agentPanel">
        <div className="panelHead">
          <h2>エージェント検索</h2>
          <span className="small">/api/agent (SSE) の動作確認用</span>
        </div>
        <div className="agentBody">
          <form
            className="agentForm"
            onSubmit={(e) => {
              e.preventDefault();
              runAgent();
            }}
          >
            <input
              className="input"
              type="text"
              value={agentQuery}
              placeholder="例: 楽天カードと年会費無料のクレカ、どっちがお得?"
              onChange={(e) => setAgentQuery(e.currentTarget.value)}
              disabled={agentBusy}
            />
            <button
              type="submit"
              className="btn primary"
              disabled={agentBusy || agentQuery.trim().length === 0}
            >
              {agentBusy ? "実行中…" : "実行"}
            </button>
          </form>

          {agentError ? <div className="agentError">{agentError}</div> : null}

          {agentTrace.length > 0 ? (
            <div className="agentSection">
              <div className="agentSectionHead">
                トレース ({agentTrace.length} ステップ)
                {agentBusy ? <span className="agentLive">実行中</span> : null}
              </div>
              <TraceTimeline trace={agentTrace} />
            </div>
          ) : null}

          {agentAnswer ? (
            <div className="agentSection">
              <div className="agentSectionHead">回答</div>
              <p className="agentAnswer">{agentAnswer}</p>
            </div>
          ) : null}

          {agentItems.length > 0 ? (
            <div className="agentSection">
              <div className="agentSectionHead">参照案件 ({agentItems.length})</div>
              <div className="tableWrap">
                <table>
                  <thead>
                    <tr>
                      <th>Site</th>
                      <th>Title</th>
                      <th>Point</th>
                      <th>URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentItems.map((item) => (
                      <tr key={`${item.site}-${item.id}`}>
                        <td>{item.site}</td>
                        <td>{item.title}</td>
                        <td>{item.pointText || "-"}</td>
                        <td>
                          <a href={item.url} target="_blank" rel="noreferrer">
                            開く
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {!agentBusy &&
          !agentError &&
          agentTrace.length === 0 &&
          !agentAnswer &&
          agentItems.length === 0 ? (
            <p className="small">
              自然文クエリを入力して「実行」を押すとエージェントを起動します。
            </p>
          ) : null}
        </div>
      </section>

      <section className="grid">
        <article className="panel">
          <div className="panelHead">
            <h2>クロールジョブ</h2>
            <span className="small">最新 {jobs.length} 件</span>
          </div>
          <div className="tableWrap scroll">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Site</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Requested</th>
                  <th>Finished</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 ? (
                  <tr>
                    <td className="tableEmpty" colSpan={7}>
                      ジョブはまだありません。
                    </td>
                  </tr>
                ) : (
                  jobs.map((job) => (
                    <tr key={job.id}>
                      <td className="num">{job.id}</td>
                      <td>{job.site}</td>
                      <td>
                        <span className={`status ${job.status}`}>{job.status}</span>
                        {job.errorMessage ? (
                          <div className="errorNote">{job.errorMessage}</div>
                        ) : null}
                      </td>
                      <td className="num">{job.attempts}</td>
                      <td className="time">{fmt(job.createdAt)}</td>
                      <td className="time">{fmt(job.finishedAt)}</td>
                      <td>
                        {job.status === "failed" ? (
                          <button
                            type="button"
                            className="btn small"
                            onClick={() => void retry(job.id)}
                            disabled={busy}
                          >
                            Retry
                          </button>
                        ) : (
                          <span className="small">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="panel">
          <div className="panelHead">
            <h2>アフィリエイトタグ</h2>
            <span className="small">案件URLに付与するクエリ文字列</span>
          </div>
          <div className="panelFilter">
            <input
              className="input"
              type="text"
              value={tagFilter}
              placeholder="サイト名で絞り込み (例: point)"
              onChange={(e) => setTagFilter(e.currentTarget.value)}
            />
            <span className="small">
              {filteredTagSites.length} / {SITES.length} サイト
            </span>
          </div>
          <div className="tableWrap scroll">
            <table>
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Tag</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredTagSites.length === 0 ? (
                  <tr>
                    <td className="tableEmpty" colSpan={3}>
                      「{tagFilter}」に一致するサイトはありません。
                    </td>
                  </tr>
                ) : (
                  filteredTagSites.map((s) => (
                    <tr key={s}>
                      <td>{s}</td>
                      <td>
                        <input
                          className="input"
                          type="text"
                          value={affiliateTags[s]}
                          placeholder="例: friend_code=abc123"
                          onChange={(e) =>
                            setAffiliateTags((prev) => ({ ...prev, [s]: e.currentTarget.value }))
                          }
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn small"
                          onClick={() => void saveAffiliateTag(s)}
                          disabled={busy}
                        >
                          保存
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="panel">
          <div className="panelHead">
            <h2>案件一覧</h2>
            <span className="small">最新 {offers.length} 件</span>
          </div>
          <div className="tableWrap scroll">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Site</th>
                  <th>Title</th>
                  <th>Point</th>
                  <th>Crawled</th>
                </tr>
              </thead>
              <tbody>
                {offers.length === 0 ? (
                  <tr>
                    <td className="tableEmpty" colSpan={5}>
                      取り込み済みの案件はまだありません。
                    </td>
                  </tr>
                ) : (
                  offers.map((offer) => (
                    <tr key={`${offer.site}-${offer.id}`}>
                      <td className="num">{offer.id}</td>
                      <td>{offer.site}</td>
                      <td>
                        <a href={offer.url} target="_blank" rel="noreferrer">
                          {offer.title}
                        </a>
                      </td>
                      <td>{offer.pointText || "-"}</td>
                      <td className="time">{fmt(offer.crawledAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>
    </main>
  );
}
